import { useNavigation } from '@react-navigation/native';
import { Card, Icon, Text, useTheme } from '@ui-kitten/components';
import { find as lodashFind, isEmpty as lodashIsEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import { Linking, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../../Components/Button';
import { RectangleLoader } from '../../../Components/Loader';
import { MODALPROMPT } from '../../../Components/Web/Modal/ModalPrompt/config';
import useModalPrompt from '../../../Components/Web/Modal/ModalPrompt/hooks/useModalPrompt';
import constants from '../../../Config/constants';
import messages from '../../../Config/messages';
import { getEnvValue } from '../../../Helper/env';
import useFavorite from '../../../Hooks/useFavorite';
import useMealPlan from '../../../Hooks/useMealPlan';
import routeList from '../../../Routes/list';
import { updateCheckoutDetails } from '../../../RTK/checkout';
import { checkoutOrderTypeSelector } from '../../../RTK/checkout/selectors';
import { checkout } from '../../../RTK/defaultValues';
import { shopSelector } from '../../../RTK/shop/selectors';
import Service from '../../../Service';
import ThemeColor from '../../../Theme/colors';
import ThemeStyle from '../../../Theme/styles';
import BreakPoints from '../../../Theme/styles/breakpoints';

import useDetailsController from '../controller/details';
import OrderTypeNote from '../OrderTypeNote';

import StoreOverview from './Overview';

const RAZZLE_BUILD_MODE = getEnvValue('RAZZLE_BUILD_MODE');
const isBranded = RAZZLE_BUILD_MODE === 'branded';
const StoreDescription = () => {
  const store_theme = useTheme();

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const orderTypeHasBeenSet = useRef();

  const shopData = useSelector(shopSelector);
  const { isLoading, isSaved, toggleFavourite } = useFavorite(shopData?.id);

  const { showStoreNotAcceptingAdvancedOrder } = useDetailsController({
    isModal: false,
  });

  const { showModalPrompt } = useModalPrompt();
  const { mealPlanDayCount } = useMealPlan();

  const selectedOrderType = useSelector((state) =>
    checkoutOrderTypeSelector(state, shopData?.id, true),
  );

  let paramOrderType = '';
  const store_id = shopData?.id;
  const distance = shopData?.distance;
  // Check if running in web
  if (constants.isWeb) {
    if (isBranded) {
      const params = new URLSearchParams(document?.location?.search); // check url params for order_type
      paramOrderType = params.get('order_type') || paramOrderType; // default order type as string
    }
  }

  const searchTagsFilter = useSelector((state) => state.filter.search.tags);
  const homeTagsFilter = useSelector((state) => state.filter.home.tags);
  const homeOrderTypeFilter = lodashFind(homeTagsFilter, {
    paramName: 'order_type',
  });

  useEffect(() => {
    if (!lodashIsEmpty(shopData) && !orderTypeHasBeenSet.current) {
      // check for previous screen only for search
      // because aside from search, other screen to access store page must use the homepage tag filter
      const routes = navigation.getState().routes; // get routes
      const prevRoutes = routes[routes.length - 2]; // get previous routes
      const isPreviousRouteSearch =
        prevRoutes?.name === routeList.SEARCH ? true : false; // is previous page is search ?
      const homeOrderTypeFilter = lodashFind(homeTagsFilter, {
        paramName: 'order_type',
      }); // get tag filter on homepage
      const searchOrderTypeFilter = lodashFind(searchTagsFilter, {
        paramName: 'order_type',
      }); // get tag filter on search page
      const tagFilter = isPreviousRouteSearch
        ? searchOrderTypeFilter
        : homeOrderTypeFilter; // if previous route is search: use the search tag filter, else home tag filter
      if (!lodashIsEmpty(tagFilter)) {
        _changeOrderTypeToSelectedTagFilter(tagFilter);
      }
      orderTypeHasBeenSet.current = true; // for flag, for this not to be called every change of menu (meal plan to non meal plan vice versa)
    }
    if (!selectedOrderType?.value) {
      _changeOrderTypeToSelectedTagFilter(homeOrderTypeFilter);
    }
  }, [shopData]);

  const _changeOrderTypeToSelectedTagFilter = (orderTypeFilter) => {
    const orderType = orderTypeFilter?.values[0].value || paramOrderType;
    const type = lodashFind(constants.ORDER_TYPE_DATA, { value: orderType });
    if (type) {
      dispatch(
        updateCheckoutDetails({
          store_id: store_id,
          keyToUpdate: checkout.keys.ORDER_TYPE,
          keyValue: type,
        }),
      );
    }
  };

  const _moreInfoPress = () =>
    showModalPrompt(MODALPROMPT.storeInformation, { shopName: shopData?.name });

  const _onButtonPressed = () => {
    const url = Service.getStoreMapUrl(
      shopData.location,
      shopData?.geocode_location?.lat,
      shopData?.geocode_location?.lng,
    );
    Linking.openURL(url);
  };

  if (lodashIsEmpty(shopData)) {
    return (
      <View
        dataSet={{ media: ids.loadingSkeletonContainer }}
        style={styles.loadingSkeletonContainer}
      >
        <View style={ThemeStyle.spacingTopMedium}>
          <RectangleLoader width={250} height={30} />
        </View>
        <View style={ThemeStyle.flexDirectionRowSpaceBetween}>
          <View style={ThemeStyle.spacingTopMedium}>
            <RectangleLoader width={150} height={20} />
          </View>
          <View style={ThemeStyle.spacingTopMedium}>
            <RectangleLoader width={100} height={20} />
          </View>
        </View>

        <View style={ThemeStyle.flexDirectionRowSpaceBetween}>
          <View>
            <View style={ThemeStyle.spacingTopMedium}>
              <RectangleLoader width={150} height={20} />
            </View>
            <View style={ThemeStyle.spacingTopMedium}>
              <RectangleLoader width={150} height={20} />
            </View>
          </View>
          <View style={styles.loading_column_2_container}>
            <View style={ThemeStyle.spacingTopMedium}>
              <RectangleLoader width={130} height={25} />
            </View>
            <View style={ThemeStyle.spacingTopMedium}>
              <RectangleLoader width={130} height={25} />
            </View>
          </View>
        </View>
      </View>
    );
  }
  return (
    <View style={styles.wrapper}>
      {/*
          store name section
          */}
      <View
        dataSet={{ media: ids.storeDescription }}
        style={styles.storeDescription}
      >
        {(shopData?.is_preview || showStoreNotAcceptingAdvancedOrder) && (
          <Card
            status="danger"
            style={styles.notAcceptingAdvanceOrderContainer}
          >
            <Text category="s2" style={styles.notAcceptingOrderTxt}>
              {shopData?.is_preview
                ? messages.STORE_PREVIEW
                : messages.STORE_NOT_ACCEPTING_ADVANCE_ORDER_MSG}
            </Text>
          </Card>
        )}

        <Text category="h5">{shopData?.name}</Text>
        <View
          dataSet={{ media: ids.storeDescriptionRow }}
          style={styles.storeDescriptionRow}
        >
          <View
            style={[
              ThemeStyle.flex1,
              ThemeStyle.flexWrap,
              ThemeStyle.spacingRightMedium,
            ]}
          >
            <StoreOverview />
          </View>
          <View
            dataSet={{ media: ids.storeButtonContainer }}
            style={styles.storeButtonContainer}
          >
            {!isBranded && (
              <Button
                testID="save_btn"
                disabled={isLoading}
                style={styles.more_info_button}
                onPress={toggleFavourite}
                plain
              >
                <Icon
                  name={isSaved ? 'heart' : 'heart-o'}
                  fill={ThemeColor.green}
                  pack="fa"
                  style={styles.control_icon}
                />
                <View
                  dataSet={{ media: ids.storeLabel }}
                  style={styles.storeLabel}
                >
                  <Text category="c1">
                    {isSaved
                      ? 'Remove from My Favorites'
                      : 'Add to My Favorites'}
                  </Text>
                </View>
              </Button>
            )}

            <Button
              testID="more_info_btn"
              onPress={_moreInfoPress}
              style={styles.more_info_button}
              plain
            >
              <Icon
                name="info"
                fill={store_theme['icon_color'] || ThemeColor.green}
                style={styles.control_icon}
              />

              <View
                dataSet={{ media: ids.storeLabel }}
                style={styles.storeLabel}
              >
                <Text category="c1">Store Info</Text>
              </View>
            </Button>
          </View>
        </View>

        <OrderTypeNote
          mealPlanDayCount={mealPlanDayCount}
          storeAddress={shopData.location}
          type={selectedOrderType?.value || paramOrderType}
          goToLocation={_onButtonPressed}
        />
      </View>
    </View>
  );
};

const { ids, styles } = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  storeDescription: {
    [`@media ${BreakPoints.xs}`]: {
      paddingHorizontal: 20,
    },
    [`@media ${BreakPoints.sm}`]: {
      paddingLeft: 20,
      marginHorizontal: 50,
    },
  },
  storeDescriptionRow: {
    ...ThemeStyle.flex1,
    ...ThemeStyle.flexDirectionRowEnd,
    [`@media ${BreakPoints.xs}`]: {
      marginRight: -20, // remove margin right on mobile due to whole container of 1st column on web
    },
  },
  storeLabel: {
    alignItems: 'flex-start',
    [`@media ${BreakPoints.xs}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.sm}`]: {
      display: 'none',
    },
    [`@media ${BreakPoints.md}`]: {
      display: 'flex',
    },
  },
  storeButtonContainer: {
    columnGap: 10,
    rowGap: 10,
    alignItems: 'flex-start',
    flexDirection: 'row',
  },
  more_info_button: {
    display: 'flex',
    borderRadius: 20,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ThemeColor.lightGray,
    borderWidth: 1,
    borderColor: ThemeColor.lightGray,
    padding: 10,
    columnGap: 5,
  },
  control_icon: { width: 21, height: 21 },
  loadingSkeletonContainer: {
    ...ThemeStyle.spacingTopLarge,
    [`@media ${BreakPoints.xs}`]: {
      paddingHorizontal: 20,
    },
    [`@media ${BreakPoints.sm}`]: {
      paddingLeft: 20,
      marginHorizontal: 50,
    },
  },
  loading_column_2_container: {
    ...ThemeStyle.flexDirectionRowSpaceBetween,
    width: '30%',
  },
  notAcceptingAdvanceOrderContainer: {
    ...ThemeStyle.spacingBottomSmall,
    backgroundColor: ThemeColor.red100,
  },
  notAcceptingOrderTxt: { textAlign: 'center' },
});

export default StoreDescription;
